import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Minus, Plus, X } from 'lucide-react';
import { BTN, STATUS } from '../styles/tokens';
import {
    AVATAR_OUTPUT_SIZE,
    clampOffset,
    computeDrawRect,
    computeGeometry,
    cropToFile,
    loadImageBitmap,
    sliderToZoom,
    sourcePixelsAt,
    zoomToSlider,
    type CropGeometry,
    type CropOffset,
} from '../utils/cropImage';

interface AvatarCropperModalProps {
    /** 待裁切的原始檔案 */
    file: File;
    onCancel: () => void;
    /** 使用者按下確定後，回傳裁好的正方形檔案 */
    onConfirm: (croppedFile: File) => void;
}

/** 滾輪與 ＋／－ 按鈕的單次縮放倍率 */
const WHEEL_STEP = 1.08;
const BUTTON_STEP = 1.2;

/**
 * 形象照裁切彈窗。
 *
 * 預覽直接畫在 canvas 上，與輸出走同一條 drawImage 路徑，所見即所得；
 * 若改用 CSS transform 縮放，高倍率時瀏覽器可能拿合成層點陣圖放大，預覽會比實際存檔更糊。
 *
 * 必須用 Portal 掛到 body：Sidebar 的 <aside> 帶有 transform，
 * 會讓 position:fixed 的子元素改以 aside 為包含塊，彈窗會被關在側欄裡。
 */
const AvatarCropperModal: React.FC<AvatarCropperModalProps> = ({ file, onCancel, onConfirm }) => {
    const stageRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // 高頻變動的互動狀態放 ref，避免每次 pointermove 都觸發 re-render
    const bitmapRef = useRef<ImageBitmap | null>(null);
    const geoRef = useRef<CropGeometry | null>(null);
    const zoomRef = useRef(1);
    const offsetRef = useRef<CropOffset>({ tx: 0, ty: 0 });
    const rafRef = useRef<number | null>(null);
    const pointersRef = useRef(new Map<number, { x: number; y: number }>());
    const dragRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
    const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);

    const [ready, setReady] = useState(false);
    const [busy, setBusy] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [cropSize, setCropSize] = useState(0);
    const [slider, setSlider] = useState(0);
    const [zoomLabel, setZoomLabel] = useState('1.0×');
    const [lowQuality, setLowQuality] = useState(false);

    // ── 繪製 ────────────────────────────────────────────────────────────────

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const bitmap = bitmapRef.current;
        const geo = geoRef.current;
        if (!canvas || !bitmap || !geo) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const { left, top, dispW, dispH } = computeDrawRect(geo, zoomRef.current, offsetRef.current);
        ctx.clearRect(0, 0, geo.stageW, geo.stageH);
        ctx.drawImage(bitmap, left, top, dispW, dispH);
    }, []);

    /** 拖曳時每個影格只重畫一次 */
    const scheduleDraw = useCallback(() => {
        if (rafRef.current !== null) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            draw();
        });
    }, [draw]);

    /** 把 ref 裡的縮放狀態同步到畫面上的滑桿、倍率文字與畫質提示 */
    const syncIndicators = useCallback(() => {
        const geo = geoRef.current;
        if (!geo) return;
        const zoom = zoomRef.current;
        setSlider(zoomToSlider(zoom, geo.maxZoom));
        setZoomLabel(`${zoom.toFixed(1)}×`);
        setLowQuality(sourcePixelsAt(geo, zoom) < AVATAR_OUTPUT_SIZE);
    }, []);

    /** 量測預覽區、重算幾何、依裝置像素比配置 canvas */
    const measure = useCallback(() => {
        const stage = stageRef.current;
        const canvas = canvasRef.current;
        const bitmap = bitmapRef.current;
        if (!stage || !canvas || !bitmap) return;

        const stageW = stage.clientWidth;
        const stageH = stage.clientHeight;
        if (stageW === 0 || stageH === 0) return;

        const geo = computeGeometry(bitmap.width, bitmap.height, stageW, stageH);
        geoRef.current = geo;
        setCropSize(geo.cropSize);

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.round(stageW * dpr);
        canvas.height = Math.round(stageH * dpr);
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
        }

        zoomRef.current = Math.min(geo.maxZoom, Math.max(1, zoomRef.current));
        offsetRef.current = clampOffset(geo, zoomRef.current, offsetRef.current);
    }, []);

    // ── 縮放 ────────────────────────────────────────────────────────────────

    const applyZoom = useCallback(
        (next: number, anchor?: { x: number; y: number }) => {
            const geo = geoRef.current;
            if (!geo) return;
            const prev = zoomRef.current;
            const zoom = Math.min(geo.maxZoom, Math.max(1, next));
            zoomRef.current = zoom;

            if (anchor) {
                // 以錨點（相對預覽區中心）為中心縮放，畫面才不會亂跳
                const k = zoom / prev;
                offsetRef.current = {
                    tx: anchor.x - (anchor.x - offsetRef.current.tx) * k,
                    ty: anchor.y - (anchor.y - offsetRef.current.ty) * k,
                };
            }
            offsetRef.current = clampOffset(geo, zoom, offsetRef.current);
            scheduleDraw();
            syncIndicators();
        },
        [scheduleDraw, syncIndicators],
    );

    // ── 拖曳與雙指縮放 ──────────────────────────────────────────────────────

    const pointerDistance = () => {
        const points = [...pointersRef.current.values()];
        if (points.length < 2) return 0;
        return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 1) {
            dragRef.current = { x: e.clientX, y: e.clientY, ...offsetRef.current };
        } else if (pointersRef.current.size === 2) {
            pinchRef.current = { dist: pointerDistance(), zoom: zoomRef.current };
            dragRef.current = null;
        }
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

        const geo = geoRef.current;
        if (!geo) return;

        if (pointersRef.current.size >= 2 && pinchRef.current) {
            const dist = pointerDistance();
            if (pinchRef.current.dist > 0) {
                applyZoom(pinchRef.current.zoom * (dist / pinchRef.current.dist));
            }
        } else if (dragRef.current) {
            const drag = dragRef.current;
            offsetRef.current = clampOffset(geo, zoomRef.current, {
                tx: drag.tx + (e.clientX - drag.x),
                ty: drag.ty + (e.clientY - drag.y),
            });
            scheduleDraw();
        }
    };

    const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) pinchRef.current = null;
        if (pointersRef.current.size === 1) {
            // 雙指放開剩一指時，以剩下那指重新起算拖曳，避免畫面瞬間跳動
            const [point] = [...pointersRef.current.values()];
            dragRef.current = { x: point.x, y: point.y, ...offsetRef.current };
        } else if (pointersRef.current.size === 0) {
            dragRef.current = null;
        }
    };

    // ── 生命週期 ────────────────────────────────────────────────────────────

    // 讀取圖片；換檔案時重置縮放與位移
    useEffect(() => {
        let cancelled = false;
        setReady(false);
        setErrorMsg(null);

        loadImageBitmap(file)
            .then(bitmap => {
                if (cancelled) {
                    bitmap.close();
                    return;
                }
                bitmapRef.current = bitmap;
                zoomRef.current = 1;
                offsetRef.current = { tx: 0, ty: 0 };
                setReady(true);
            })
            .catch(() => {
                if (!cancelled) setErrorMsg('這張圖片無法讀取，請換一張試試。');
            });

        return () => {
            cancelled = true;
            bitmapRef.current?.close();
            bitmapRef.current = null;
            geoRef.current = null;
        };
    }, [file]);

    // 預覽區尺寸變化時重新量測（初次 observe 也會觸發一次，等同初始化）
    useEffect(() => {
        const stage = stageRef.current;
        if (!ready || !stage) return;
        const observer = new ResizeObserver(() => {
            measure();
            scheduleDraw();
            syncIndicators();
        });
        observer.observe(stage);
        return () => observer.disconnect();
    }, [ready, measure, scheduleDraw, syncIndicators]);

    // 滾輪縮放。React 的 onWheel 是 passive listener，preventDefault 無效，只能自己掛
    useEffect(() => {
        const stage = stageRef.current;
        if (!ready || !stage) return;
        const onWheel = (e: WheelEvent) => {
            const geo = geoRef.current;
            if (!geo) return;
            e.preventDefault();
            const rect = stage.getBoundingClientRect();
            applyZoom(zoomRef.current * (e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP), {
                x: e.clientX - rect.left - geo.stageW / 2,
                y: e.clientY - rect.top - geo.stageH / 2,
            });
        };
        stage.addEventListener('wheel', onWheel, { passive: false });
        return () => stage.removeEventListener('wheel', onWheel);
    }, [ready, applyZoom]);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onCancel]);

    useEffect(() => {
        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    // ── 送出 ────────────────────────────────────────────────────────────────

    const handleConfirm = async () => {
        const bitmap = bitmapRef.current;
        const geo = geoRef.current;
        if (!bitmap || !geo || busy) return;
        setBusy(true);
        try {
            const cropped = await cropToFile(bitmap, geo, zoomRef.current, offsetRef.current, file);
            onConfirm(cropped);
        } catch {
            setErrorMsg('裁切失敗，請再試一次。');
            setBusy(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onCancel}
            role="dialog"
            aria-modal="true"
            aria-label="調整形象照"
        >
            <div
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-4 md:p-5 flex flex-col gap-4"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white">調整形象照</h3>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                        aria-label="關閉"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div
                    ref={stageRef}
                    className="relative w-full aspect-square max-h-[46vh] rounded-xl overflow-hidden bg-slate-900 touch-none select-none cursor-grab active:cursor-grabbing"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerEnd}
                    onPointerCancel={handlePointerEnd}
                >
                    <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
                    {cropSize > 0 && (
                        <div
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/90 pointer-events-none"
                            style={{ width: cropSize, height: cropSize, boxShadow: '0 0 0 9999px rgba(2,6,23,0.68)' }}
                        />
                    )}
                    {!ready && !errorMsg && (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-300">
                            載入中…
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => applyZoom(zoomRef.current / BUTTON_STEP)}
                        className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${BTN.neutral}`}
                        aria-label="縮小"
                    >
                        <Minus size={18} />
                    </button>
                    <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.001}
                        value={slider}
                        onChange={e => {
                            const geo = geoRef.current;
                            if (geo) applyZoom(sliderToZoom(Number(e.target.value), geo.maxZoom));
                        }}
                        className="flex-1 min-w-0 accent-blue-600"
                        aria-label="縮放"
                    />
                    <button
                        type="button"
                        onClick={() => applyZoom(zoomRef.current * BUTTON_STEP)}
                        className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${BTN.neutral}`}
                        aria-label="放大"
                    >
                        <Plus size={18} />
                    </button>
                    <span className="w-12 text-right text-xs font-bold text-slate-500 dark:text-slate-400 tabular-nums">
                        {zoomLabel}
                    </span>
                </div>

                <p className="text-xs text-center text-slate-400 dark:text-slate-500">
                    拖曳照片調整位置，圓框內就是最後呈現的範圍
                </p>

                {lowQuality && (
                    <p className="text-xs text-center font-semibold text-amber-600 dark:text-amber-400">
                        放這麼大時，儲存後的照片會比較模糊
                    </p>
                )}

                {errorMsg && (
                    <p className={`text-xs rounded-lg px-3 py-2 ${STATUS.error}`}>{errorMsg}</p>
                )}

                <div className="grid grid-cols-[1fr_1.4fr] gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className={`px-4 py-3 rounded-xl text-sm font-bold ${BTN.neutral}`}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!ready || busy}
                        className={`px-4 py-3 rounded-xl text-sm font-bold ${BTN.primary}`}
                    >
                        {busy ? '處理中…' : '確定儲存'}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AvatarCropperModal;
